/**
 * Shared list/grid "view" substate — search, date range, filters,
 * pagination, sort, and load status — for redux slices backing a paginated
 * table or grid.
 *
 * This shape was defined once inside `createEntitySlice` (utils/slice.js)
 * and then copy-pasted inline wherever a slice needed the same view state
 * without the rest of createEntitySlice's entity-lifecycle wiring (e.g. the
 * hand-rolled `orderSales` slice). Extracting it lets both call shapes share
 * one definition: `createEntitySlice` spreads `createViewReducers()` into
 * its own `reducers`, and a hand-rolled `createSlice` can do the same.
 *
 * Contains:
 * - initialViewState — the view substate's default shape
 * - createViewReducers — the matching reducers, keyed to mutate `state.view`
 */

export const initialViewState = {
  search: null,
  start: null,
  end: null,
  filters: {},
  page: 0,
  pageSize: 10,
  order: 'asc',
  orderBy: 'id',
  loading: false,
  lastLoaded: null,
};

/**
 * Builds the reducers for the `view` substate above. Spread the result into
 * a slice's `reducers` object — action types come from the host slice's own
 * `name`, so this stays a plain reducer map with no naming of its own.
 *
 * @returns {Object<string, Function>} reducers keyed exactly as the
 *   pre-extraction inline blocks (setPage, setPageSize, setSearch, setOrder,
 *   setOrderBy, setStart, setEnd, setFilters, setLastLoaded, setIsLoading)
 */
export const createViewReducers = () => ({
  setPage(state, action) {
    state.view.page = action.payload;
  },
  setPageSize(state, action) {
    state.view.pageSize = action.payload;
  },
  setSearch(state, action) {
    state.view.search = action.payload;
  },
  setOrder(state, action) {
    state.view.order = action.payload;
  },
  setOrderBy(state, action) {
    state.view.orderBy = action.payload;
  },
  setStart(state, action) {
    state.view.start = action.payload;
  },
  setEnd(state, action) {
    state.view.end = action.payload;
  },
  setFilters(state, action) {
    state.view.filters = action.payload;
  },
  setLastLoaded(state, action) {
    state.view.lastLoaded = action.payload;
  },
  setIsLoading(state, action) {
    state.view.loading = action.payload;
  },
});
